import React from 'react';

/**
 * A centralized panel for all adjustments.
 * It shows different controls based on the current context
 * (e.g., global settings, single-page adjustments, multi-page adjustments).
 */
const AdjustmentPanel = ({
  filters,
  onFilterChange,
  pageSettingMode,
  currentPageNumber,
  // ... other props will be added as needed
}) => {
  // Check if global adjustment has been defined (non-default values)
  const hasGlobalAdjustment = () => {
    const defaultFilters = {
      invert: false,
      grayscale: false,
      brightness: 100,
      contrast: 100,
      removeBackground: false,
      bgRemovalTolerance: 0.15,
      bgSoftenEdges: false,
    };

    return Object.keys(defaultFilters).some(key => {
      return filters[key] !== defaultFilters[key];
    });
  };

  return (
    <div className="adjustment-panel p-4 bg-gray-100 dark:bg-gray-800 rounded-lg shadow-lg">
      <h3 className="text-lg font-bold mb-4">Adjustments</h3>
      {pageSettingMode === 'preserve' && (
        <div>
          <h4 className="text-md font-semibold mb-2">Preserve Original</h4>
          <button
            onClick={() => {
              // Check for unsaved changes
              if (!checkUnsavedChanges('preserve')) return;

              // Ensure preview is cleared when entering Preserve mode
              if (setPreviewFilters && setPreviewPages) {
                setPreviewFilters(null);
                setPreviewPages(new Set());
              }

              // Mobile: Apply preserve directly without opening bottom sheet
              const isMobile = window.innerWidth < 768;
              if (isMobile) {
                // If already selected, toggle off
                if (pageSettingMode === 'preserve') {
                  // Clear manual flag to allow auto-detection to run after deletion
                  setManualModeChange(false);
                  setPageSettingMode('');
                  setModeWasAutoSelected(false);
                  // Delete exceptions
                  if (onDeleteException) {
                    const pagesToClear = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                      ? Array.from(selectedContactSheetPages)
                      : [currentPageNumber];

                    pagesToClear.forEach(pageNum => {
                      onDeleteException(pageNum);
                    });
                  }
                } else {
                  // Not selected, so select it
                  setManualModeChange(true);
                  setModeWasAutoSelected(false);
                  setPageSettingMode('preserve');
                  if (onPreservePage) {
                    const pagesToPreserve = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                      ? Array.from(selectedContactSheetPages)
                      : [currentPageNumber];
                    onPreservePage(pagesToPreserve);
                  }
                }
                return;
              }

              // Desktop: If already selected, toggle off
              if (pageSettingMode === 'preserve') {
                // Clear manual flag to allow auto-detection to run after deletion
                setManualModeChange(false);
                setPageSettingMode('');
                setModeWasAutoSelected(false);
                // Delete exceptions - auto-detection will run after and keep state synced
                if (onDeleteException) {
                  const pagesToClear = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                    ? Array.from(selectedContactSheetPages)
                    : [currentPageNumber];

                  pagesToClear.forEach(pageNum => {
                    onDeleteException(pageNum);
                  });
                }
              } else {
                // Not selected, so select it
                setManualModeChange(true);
                setModeWasAutoSelected(false);
                setPageSettingMode('preserve');
                if (onPreservePage) {
                  // Apply to selected pages in Contact Sheet mode, or current page in Preview mode
                  const pagesToPreserve = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                    ? Array.from(selectedContactSheetPages)
                    : [currentPageNumber];
                  onPreservePage(pagesToPreserve);
                }
              }
            }}
            className={`relative h-full px-2 py-2 md:px-3 md:py-2.5 rounded-lg border-2 transition-all flex flex-col md:flex-row items-center justify-center md:text-left ${
              pageSettingMode === 'preserve'
                ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30 dark:border-amber-600'
                : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-amber-300 dark:hover:border-amber-500'
            }`}
          >
            {/* Mobile: Icon + label, Desktop: Full layout */}
            <div className="flex flex-col items-center gap-0.5 md:hidden">
              <svg className="w-5 h-5 flex-shrink-0 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className="text-[10px] font-medium text-gray-700 dark:text-gray-200">Preserve</span>
            </div>
            <div className="hidden md:flex md:flex-row md:items-center gap-2 w-full">
              <svg className="w-5 h-5 flex-shrink-0 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 leading-tight">
                  {currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 1
                    ? 'Preserve Original Pages'
                    : 'Preserve Original Page'}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-300">
                  {currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 1
                    ? 'Keep selected pages unchanged'
                    : 'Keep this page unchanged'}
                </p>
              </div>
              <div className="flex flex-col items-end gap-0.5">
                {pageSettingMode === 'preserve' && <span className="text-amber-600 dark:text-amber-400 text-base">✓</span>}
              </div>
            </div>
          </button>
        </div>
      )}
      {pageSettingMode === 'global' && (
        <div>
          <h4 className="text-md font-semibold mb-2">Global Settings</h4>
          <div className="space-y-3">
            {/* Toggles Row */}
            <div className="grid grid-cols-3 gap-3">
              {/* Invert */}
              <div>
                <label className="text-xs font-medium text-gray-800 dark:text-gray-100 block mb-1">Invert</label>
                <button
                  onClick={() => handleFilterChange('invert', !filters.invert)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    filters.invert ? 'bg-indigo-600 dark:bg-indigo-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    filters.invert ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Grayscale */}
              <div>
                <label className="text-xs font-medium text-gray-800 dark:text-gray-100 block mb-1">Grayscale</label>
                <button
                  onClick={() => handleFilterChange('grayscale', !filters.grayscale)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    filters.grayscale ? 'bg-indigo-600 dark:bg-indigo-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    filters.grayscale ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Remove BG */}
              <div>
                <label className="text-xs font-medium text-gray-800 dark:text-gray-100 block mb-1">Remove BG</label>
                <button
                  onClick={() => handleFilterChange('removeBackground', !filters.removeBackground)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    filters.removeBackground ? 'bg-indigo-600 dark:bg-indigo-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    filters.removeBackground ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>

            {/* Sliders - 2 Columns */}
            <div className="grid grid-cols-2 gap-3">
              {/* Brightness */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs font-medium text-gray-800 dark:text-gray-100">Brightness</label>
                  <span className="text-xs text-gray-600 dark:text-gray-300">{filters.brightness}%</span>
                </div>
                <input
                  type="range"
                  min="50"
                  max="200"
                  value={filters.brightness}
                  onChange={(e) => handleFilterChange('brightness', Number(e.target.value))}
                  className="w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                />
              </div>

              {/* Contrast */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs font-medium text-gray-800 dark:text-gray-100">Contrast</label>
                  <span className="text-xs text-gray-600 dark:text-gray-300">{filters.contrast}%</span>
                </div>
                <input
                  type="range"
                  min="50"
                  max="200"
                  value={filters.contrast}
                  onChange={(e) => handleFilterChange('contrast', Number(e.target.value))}
                  className="w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                />
              </div>
            </div>

            {/* Action Buttons */}
            {(() => {
              // Calculate button visibility logic
              const selectedPages = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                ? Array.from(selectedContactSheetPages)
                : [currentPageNumber];

              const allPagesSelected = totalPages && selectedPages.length === totalPages;

              // Count how many selected pages have global
              const selectedWithGlobal = selectedPages.filter(pageNum =>
                pageExceptions[pageNum]?.enabled && pageExceptions[pageNum]?.useGlobal
              ).length;

              const allSelectedHaveGlobal = selectedWithGlobal === selectedPages.length && selectedPages.length > 0;

              // Count total pages with global (across entire document)
              const totalPagesWithGlobal = Object.keys(pageExceptions).filter(pageNum =>
                pageExceptions[pageNum]?.enabled && pageExceptions[pageNum]?.useGlobal
              ).length;

              // Check if there are unadjusted pages (pages without any adjustment)
              const hasUnadjustedPages = totalPages && totalPagesWithGlobal < totalPages;

              // Determine if "Remove from Selected" and "Remove from All" are the same action
              const removeButtonsAreRedundant = allPagesSelected && allSelectedHaveGlobal;

              return (
                <div className="space-y-3">
                  {/* Cancel Button - Always show at top */}
                  <button
                    onClick={() => {
                      // Restore original filters via parent callback
                      onCancelGlobalAdjustment?.(handleSetManualModeChange);
                      setBottomSheetOpen(false);
                    }}
                    className="w-full py-2 bg-gray-500 dark:bg-gray-600 text-white rounded-lg text-sm font-semibold hover:bg-gray-600 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>

                  {/* When both remove and apply single buttons show, put them on same line */}
                  {pageSettingMode === 'global' && removeButtonsAreRedundant && !hasUnadjustedPages ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setManualModeChange(true);
                          setPageSettingMode('');
                          setModeWasAutoSelected(false);
                          if (onDeleteException) {
                            // Remove global from all pages (they're all selected and all have global)
                            Object.keys(pageExceptions).forEach(pageNum => {
                              const pageException = pageExceptions[pageNum];
                              if (pageException?.enabled && pageException?.useGlobal) {
                                onDeleteException(Number(pageNum));
                              }
                            });
                          }
                          setBottomSheetOpen(false);
                        }}
                        className="flex-1 py-2 bg-red-600 dark:bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-700 dark:hover:bg-red-600 transition-colors"
                      >
                        <div className="leading-tight">
                          Remove from<br />Selected (All)
                        </div>
                      </button>

                      <button
                        onClick={() => {
                          setManualModeChange(true);
                          setPageSettingMode('global');
                          // Apply to selected pages
                          if (onApplyGlobalToPages) {
                            const pagesToApply = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                              ? Array.from(selectedContactSheetPages)
                              : [currentPageNumber];
                            onApplyGlobalToPages(pagesToApply);
                          }
                          setBottomSheetOpen(false);
                        }}
                        className="flex-1 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                      >
                        <div className="leading-tight">
                          {allPagesSelected ? (<>Apply to<br />Selected (All)</>) : (<>Apply to<br />Selected</>)}
                        </div>
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Remove Buttons - show when global is applied to selection */}
                      {pageSettingMode === 'global' && removeButtonsAreRedundant && (
                        /* Single remove button without panel when all pages selected and all have global */
                        <button
                          onClick={() => {
                            setManualModeChange(true);
                            setPageSettingMode('');
                            setModeWasAutoSelected(false);
                            if (onDeleteException) {
                              // Remove global from all pages (they're all selected and all have global)
                              Object.keys(pageExceptions).forEach(pageNum => {
                                const pageException = pageExceptions[pageNum];
                                if (pageException?.enabled && pageException?.useGlobal) {
                                  onDeleteException(Number(pageNum));
                                }
                              });
                            }
                            setBottomSheetOpen(false);
                          }}
                          className="w-full py-2 bg-red-600 dark:bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-700 dark:hover:bg-red-600 transition-colors"
                        >
                          <div className="leading-tight">
                            Remove from<br />Selected (All)
                          </div>
                        </button>
                      )}

                      {/* Remove Panel - show when there are two distinct remove actions */}
                      {pageSettingMode === 'global' && !removeButtonsAreRedundant && (
                        <div className="border-2 border-red-200 dark:border-red-800 rounded-lg p-2 bg-red-50/50 dark:bg-red-900/20">
                          <p className="text-[10px] font-semibold text-red-700 dark:text-red-300 mb-1.5 px-1">Remove Global:</p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setManualModeChange(true);
                                setPageSettingMode('');
                                setModeWasAutoSelected(false);
                                if (onDeleteException) {
                                  const pagesToClear = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                                    ? Array.from(selectedContactSheetPages)
                                    : [currentPageNumber];
                                  pagesToClear.forEach(pageNum => {
                                    const pageException = pageExceptions[pageNum];
                                    // Only delete if it's a global exception (has useGlobal flag)
                                    if (pageException?.enabled && pageException?.useGlobal) {
                                      onDeleteException(pageNum);
                                    }
                                  });
                                }
                                setBottomSheetOpen(false);
                              }}
                              className="flex-1 py-2 bg-red-600 dark:bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-700 dark:hover:bg-red-600 transition-colors"
                            >
                              <div className="leading-tight">
                                Remove from<br />Selected
                              </div>
                            </button>

                            <button
                              onClick={() => {
                                setManualModeChange(true);
                                setPageSettingMode('');
                                setModeWasAutoSelected(false);
                                if (onDeleteException) {
                                  // Remove global from ALL pages that have it
                                  Object.keys(pageExceptions).forEach(pageNum => {
                                    const pageException = pageExceptions[pageNum];
                                    // Only delete if it's a global exception (has useGlobal flag)
                                    if (pageException?.enabled && pageException?.useGlobal) {
                                      onDeleteException(Number(pageNum));
                                    }
                                  });
                                }
                                setBottomSheetOpen(false);
                              }}
                              className="flex-1 py-2 bg-red-700 dark:bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-800 dark:hover:bg-red-700 transition-colors"
                            >
                              <div className="leading-tight">
                                Remove from<br />All Pages
                              </div>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Apply Buttons - always show at least "Apply to Selected" since user might modify controls */}
                      {hasUnadjustedPages && !allPagesSelected && (
                        /* Panel with both apply buttons when there are unadjusted pages AND not all pages selected */
                        <div className="border-2 border-blue-200 dark:border-blue-800 rounded-lg p-2 bg-blue-50/50 dark:bg-blue-900/20">
                          <p className="text-[10px] font-semibold text-blue-700 dark:text-blue-300 mb-1.5 px-1">Apply Global:</p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setManualModeChange(true);
                                setPageSettingMode('global');
                                // Apply to selected pages
                                if (onApplyGlobalToPages) {
                                  const pagesToApply = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                                    ? Array.from(selectedContactSheetPages)
                                    : [currentPageNumber];
                                  onApplyGlobalToPages(pagesToApply);
                                }
                                setBottomSheetOpen(false);
                              }}
                              className="flex-1 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                            >
                              <div className="leading-tight">
                                Apply to<br />Selected
                              </div>
                            </button>

                            <button
                              onClick={() => {
                                setManualModeChange(true);
                                setPageSettingMode('global');
                                if (onSaveAsGlobal) {
                                  onSaveAsGlobal(filters);
                                }
                                setBottomSheetOpen(false);
                              }}
                              className="flex-1 py-2 bg-green-600 dark:bg-green-500 text-white rounded-lg text-sm font-semibold hover:bg-green-700 dark:hover:bg-green-600 transition-colors"
                            >
                              <div className="leading-tight">
                                Apply to All<br />Unadjusted
                              </div>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Single "Apply to All" button when all pages selected and there are unadjusted pages */}
                      {hasUnadjustedPages && allPagesSelected && (
                        <button
                          onClick={() => {
                            setManualModeChange(true);
                            setPageSettingMode('global');
                            // Apply to all pages
                            if (onApplyGlobalToPages) {
                              const pagesToApply = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                                ? Array.from(selectedContactSheetPages)
                                : [currentPageNumber];
                              onApplyGlobalToPages(pagesToApply);
                            }
                            setBottomSheetOpen(false);
                          }}
                          className="w-full py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                        >
                          <div className="leading-tight">
                            Apply to<br />Selected (All)
                          </div>
                        </button>
                      )}

                      {/* Single "Apply to Selected" button without panel when no unadjusted pages */}
                      {!hasUnadjustedPages && (
                        <button
                          onClick={() => {
                            setManualModeChange(true);
                            setPageSettingMode('global');
                            // Apply to selected pages
                            if (onApplyGlobalToPages) {
                              const pagesToApply = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                                ? Array.from(selectedContactSheetPages)
                                : [currentPageNumber];
                              onApplyGlobalToPages(pagesToApply);
                            }
                            setBottomSheetOpen(false);
                          }}
                          className="w-full py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                        >
                          <div className="leading-tight">
                            {allPagesSelected ? (<>Apply to<br />Selected (All)</>) : (<>Apply to<br />Selected</>)}
                          </div>
                        </button>
                      )}
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}
      {pageSettingMode === 'custom' && (
        <div>
          <h4 className="text-md font-semibold mb-2">
            Custom Adjustments for Page {currentPageNumber}
          </h4>
          {/* Custom filter controls will go here */}
        </div>
      )}
      {!pageSettingMode && (
        <div className="text-center text-gray-500">
          <p>Select a page or adjust global settings to see options here.</p>
        </div>
      )}
    </div>
  );
};

export default AdjustmentPanel;